import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import { 
  Search, 
  Loader2, 
  Image as ImageIcon,
  X,
  Calendar,
  Tag,
  User,
  ArrowLeft
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { da } from 'date-fns/locale';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to get the correct image URL
const getImageUrl = (image, token, thumbnail = false) => {
  // If we have a drive_file_id, use the proxy endpoint with token
  if (image.drive_file_id && token) {
    const thumbnailParam = thumbnail ? '&thumbnail=true' : '';
    return `${API}/product-images/proxy/${image.drive_file_id}?token=${token}${thumbnailParam}`;
  }
  // Fallback to drive_url or url
  return image.drive_url || image.url;
};

// Lazy loading image component with IntersectionObserver
const LazyImage = ({ image, token, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' } // Start loading 100px before visible
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Card 
      ref={imgRef}
      className="group cursor-pointer overflow-hidden border-slate-200 hover:shadow-lg transition-shadow"
      onClick={onClick}
      data-testid={`image-card-${image.id}`}
    >
      <div className="aspect-square relative bg-slate-100">
        {!isInView ? (
          // Placeholder before in view
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="h-8 w-8 text-slate-300" />
          </div>
        ) : (
          <>
            {!isLoaded && !hasError && (
              // Loading state
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            )}
            <img
              src={getImageUrl(image, token, true)}
              alt={image.product_type || image.filename || 'Produkt billede'}
              className={`w-full h-full object-cover group-hover:scale-105 transition-transform ${
                isLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              loading="lazy"
              onLoad={() => setIsLoaded(true)}
              onError={(e) => {
                if (!hasError) {
                  setHasError(true);
                  // Fallback to full image if thumbnail fails
                  e.target.src = getImageUrl(image, token, false);
                }
              }}
            />
          </>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="absolute bottom-0 left-0 right-0 p-3 text-white opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="font-medium text-sm truncate">{image.product_type || 'Ukendt'}</p>
          <p className="text-xs text-white/80">{getCategoryLabel(image.category)}</p>
        </div>
      </div>
    </Card>
  );
};

const getCategoryLabel = (cat) => {
  const labels = {
    'rullegardiner': 'Rullegardiner',
    'lamelgardiner': 'Lamelgardiner',
    'plissegardiner': 'Plisségardiner',
    'persienner': 'Persienner',
    'gardinstænger': 'Gardinstænger',
    'markiser': 'Markiser',
    'solafskærmning': 'Solafskærmning',
    'tilbehør': 'Tilbehør',
    'andet': 'Andet'
  };
  return labels[cat] || cat;
};

const ImageGalleryPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    loadImages();
  }, [categoryFilter]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    
    const timeout = setTimeout(() => {
      loadImages();
    }, 300);
    
    setSearchTimeout(timeout);
    
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const loadImages = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (categoryFilter && categoryFilter !== 'all') {
        params.category = categoryFilter;
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      const response = await axios.get(`${API}/product-images`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setImages(response.data.images || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to load images:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
      return format(parseISO(isoString), "d. MMM yyyy", { locale: da });
    } catch (e) {
      return isoString;
    }
  };

  if (loading && images.length === 0) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-48" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
            <ImageIcon className="h-6 w-6 text-slate-700" />
            Produktbilleder
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Billeder uploadet fra installationer
          </p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Søg i produkttype, noter..."
            className="pl-10"
            data-testid="image-search-input"
          />
        </div>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
            <SelectValue placeholder="Alle kategorier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle kategorier</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {getCategoryLabel(cat)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Image Count */}
      <div className="text-sm text-slate-500">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
        ) : (
          `${images.length} billeder`
        )}
      </div>

      {/* Image Grid */}
      {images.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <ImageIcon className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Ingen billeder fundet</p>
            {searchQuery && (
              <p className="text-sm text-slate-400 mt-1">
                Prøv at ændre din søgning
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {images.map((image) => (
            <LazyImage 
              key={image.id}
              image={image}
              token={token}
              onClick={() => setSelectedImage(image)}
            />
          ))}
        </div>
      )}

      {/* Image Detail Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {selectedImage && (
            <div>
              <div className="relative bg-slate-900">
                {/* Full resolution image in modal */}
                <img
                  src={getImageUrl(selectedImage, token, false)}
                  alt={selectedImage.product_type || selectedImage.filename || 'Produkt billede'}
                  className="w-full max-h-[60vh] object-contain"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white"
                  onClick={() => setSelectedImage(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-4 space-y-3">
                <h3 className="font-semibold text-lg">
                  {selectedImage.product_type || 'Ukendt produkt'}
                </h3>
                
                <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                  <div className="flex items-center gap-1.5">
                    <Tag className="h-4 w-4" />
                    {getCategoryLabel(selectedImage.category)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-4 w-4" />
                    {formatDate(selectedImage.uploaded_at)}
                  </div>
                  {selectedImage.uploader_name && (
                    <div className="flex items-center gap-1.5">
                      <User className="h-4 w-4" />
                      {selectedImage.uploader_name}
                    </div>
                  )}
                </div>
                
                {selectedImage.notes && (
                  <p className="text-slate-600 text-sm bg-slate-50 p-3 rounded-lg">
                    {selectedImage.notes}
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageGalleryPage;
